import { AlertTriangle, ShieldAlert } from "lucide-react"
import { useTranslation } from "react-i18next"
import { PlanUsageStatus } from "../../Objects/Company/PlanUsageStatus"
import Animate, { AnimationTypes } from "../Animate/Animate"

interface Props {
  planUsageStatus: PlanUsageStatus
}

export function PlanUsageAlert({ planUsageStatus }: Props) {
  const { t } = useTranslation("order")

  if (!planUsageStatus.isNearLimit && !planUsageStatus.isLimitReached) {
    return null
  }

  const isLimit = planUsageStatus.isLimitReached

  const metrics = [
    {
      key: "orders",
      label: t("label_plan_orders_usage", {
        used: planUsageStatus.currentMonthOrders,
        limit: planUsageStatus.monthlyOrdersLimit,
        percent: planUsageStatus.ordersUsagePercent,
      }),
      percent: planUsageStatus.ordersUsagePercent,
      visible: planUsageStatus.isOrdersNearLimit || planUsageStatus.isOrdersLimitReached,
    },
    {
      key: "products",
      label: t("label_plan_products_usage", {
        used: planUsageStatus.currentProducts,
        limit: planUsageStatus.productsLimit,
        percent: planUsageStatus.productsUsagePercent,
      }),
      percent: planUsageStatus.productsUsagePercent,
      visible: planUsageStatus.isProductsNearLimit || planUsageStatus.isProductsLimitReached,
    },
  ].filter((metric) => metric.visible)

  return (
    <Animate type={AnimationTypes.fadeInTraslate}>
      <div
        className={`w-full mt-2 rounded-[0.5rem] ring-2 p-4 shadow-sm transition-all ${
          isLimit ? "ring-red-200" : "ring-orange-200"
        }`}
      >
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-xl ${isLimit ? "bg-red-100" : "bg-orange-100"}`}>
            {isLimit ? (
              <ShieldAlert className="w-5 h-5 text-red-600" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            )}
          </div>

          <div className="flex-1">
            <div className="space-y-3">
              {metrics.map((metric) => (
                <div key={metric.key}>
                  <p className={`text-sm font-semibold ${isLimit ? "text-red-700" : "text-orange-700"}`}>
                    {metric.label}
                  </p>

                  <div className="mt-2 w-full h-2 bg-black/20 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${isLimit ? "bg-red-500" : "bg-orange-500"}`}
                      style={{ width: `${Math.min(metric.percent, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <p className={`text-xs mt-1 ${isLimit ? "text-red-600" : "text-gray-800"}`}>
              {isLimit ? t("message_plan_limit_reached_any") : t("message_plan_near_limit_any")}
            </p>
          </div>
        </div>
      </div>
    </Animate>
  )
}
